import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import { KIND } from 'baseui/button';
import { Select } from '@tigergraph/app-ui-lib/select';

import { Drawer, DrawerAction, DrawerBody, DrawerHeader } from '@/components/Drawer';
import { showToast } from '@/components/styledToasterContainer';
import { getErrorMessage } from '@/utils/utils';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useMutationCreateQuickInsights } from '@/insights/cloud/hook';
import Metric1 from './icon/metric1.svg';
import Metric2 from './icon/metric2.svg';
import Metric3 from './icon/metric3.svg';
import Metric4 from './icon/metric4.svg';
import { GenerateInsights } from '@/pages/home/icons.tsx';
import { DrawerHeadContainer } from '@/lib/styled.tsx';
import CheckButton from '@/components/CheckButton';
import { useQuery } from 'react-query';
import { ID_TOKEN_KEY } from '@/contexts/userContext';
import { fetchGraphList } from '@/lib/instance_api';
import { FormControl } from '@/components/FormControl';
import { useState } from 'react';
import { Desc } from '@/pages/workgroup/form/StyledComponent';
import { AxiosError } from 'axios';

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  databaseName?: string;
  workspace_id: string;
  workgroup_id: string;
  nginx_host: string;
};

export default function CreateQuickInsights({
  isOpen,
  onClose,
  databaseName,
  workspace_id,
  workgroup_id,
  nginx_host,
}: Props) {
  const [css, theme] = useStyletron();

  const navigate = useNavigate();

  const createQuickInsightsMutation = useMutationCreateQuickInsights();

  const [graph, setGraph] = useState('');

  const buttonClassName = css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'center',
    padding: '10px',
    opacity: '1 !important',
  });

  const { data: graphs, isLoading } = useQuery(
    ['graph_list', nginx_host],
    () => fetchGraphList(nginx_host, sessionStorage.getItem(ID_TOKEN_KEY)!),
    {
      onSuccess(data) {
        if (data && data.length > 0) {
          setGraph(data[0]);
        }
      },
      onError(err) {
        showToast({
          kind: 'negative',
          message: getErrorMessage(err as AxiosError),
        });
      },
    }
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose}>
      <DrawerHeader>
        <DrawerHeadContainer>
          <GenerateInsights />
          Generate Data Profile for
        </DrawerHeadContainer>
        <span
          className={css({
            ...theme.typography.HeadingSmall,
          })}
        >
          {databaseName}
        </span>
      </DrawerHeader>
      <DrawerBody>
        <Desc
          $style={{
            marginTop: '0px',
          }}
        >
          Data Profile harnesses the power of advanced algorithms to provide users with valuable initial information
          about their data, all without any manual effort. By analyzing the data behind the scenes, Data Profile
          automatically generates meaningful insights and presents them in an easily understandable format.
        </Desc>
        <Desc
          $style={{
            marginTop: '12px',
          }}
        >
          With Data Profile, users can quickly gain a high-level understanding of their data without diving into complex
          analysis or writing code. The algorithms employed by Auto Insights identify patterns, trends, and key metrics,
          allowing users to grasp important aspects of their data at a glance.
        </Desc>
        <form
          className={css({
            marginTop: '12px',
          })}
        >
          <FormControl label="Insights Metrics">
            <>
              <Desc
                className={css({
                  marginBottom: '12px',
                })}
              >
                Select the metrics you want to generate insights for.
              </Desc>
              <div
                className={css({
                  display: 'grid',
                  gap: '12px 15px',
                  gridTemplateColumns: '1fr 1fr',
                })}
              >
                <CheckButton selected={true} disabled={true} className={buttonClassName}>
                  <span>Number of vertices</span>
                  <img src={Metric1} width={32} height={32} />
                </CheckButton>
                <CheckButton selected={true} disabled={true} className={buttonClassName}>
                  Number of edges
                  <img src={Metric2} width={32} height={32} />
                </CheckButton>
                <CheckButton selected={true} disabled={true} className={buttonClassName}>
                  Degree distribution
                  <img src={Metric3} width={32} height={32} />
                </CheckButton>
                <CheckButton selected={true} disabled={true} className={buttonClassName}>
                  Average degree
                  <img src={Metric4} width={32} height={32} />
                </CheckButton>
              </div>
            </>
          </FormControl>
          <FormControl
            label="Select graph"
            error={
              !isLoading && graphs?.length === 0
                ? 'There is no graph available in the database to generate insights.'
                : null
            }
          >
            <>
              <Desc
                className={css({
                  marginBottom: '12px',
                })}
              >
                Select the graph you want to generate insights for.
              </Desc>
              <Select
                isLoading={isLoading}
                options={graphs?.map((graph) => ({ id: graph, name: graph })) || []}
                value={[{ id: graph }]}
                labelKey="name"
                valueKey="id"
                onChange={(params) => {
                  if (params.value[0]) {
                    setGraph(params.value[0].id as string);
                  }
                }}
                clearable={false}
                size="compact"
              />
            </>
          </FormControl>
        </form>
      </DrawerBody>
      <DrawerAction>
        <Button type="button" onClick={onClose} kind={KIND.tertiary}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!graph || createQuickInsightsMutation.isLoading}
          onClick={async () => {
            createQuickInsightsMutation.mutate(
              { workgroup_id, workspace_id, graph },
              {
                onSuccess: async (data) => {
                  showToast({
                    kind: 'positive',
                    message: data.Message,
                  });

                  onClose();

                  navigate({
                    pathname: `/groups/${workgroup_id}`,
                    search: `?${createSearchParams({
                      tab: 'dataprofiles',
                    })}`,
                  });
                },
                onError: (error) => {
                  showToast({
                    kind: 'negative',
                    message: getErrorMessage(error),
                  });
                },
              }
            );
          }}
          isLoading={createQuickInsightsMutation.isLoading}
        >
          Generate
        </Button>
      </DrawerAction>
    </Drawer>
  );
}
